import { Component } from 'react'
import * as cn from 'classnames'
import * as get from 'lodash/get'
import * as isNumber from 'lodash/isNumber'
import * as styles from 'styles/molecules/tables/TransactionRowMobile.css'
import * as commonRowMobileStyles from 'styles/molecules/tables/CommonRowMobile.css'
import { Transaction, FullMoneyflowTyped } from 'interfaces/moneyflow'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import AbsentValue from 'components/atoms/AbsentValue'
import { TooltipIcon } from 'components/features/pfm/Tooltip/TooltipIcon'
import TextButton from 'components/atoms/buttons/TextButton'
import { isStatusCardTransaction, isTransactionEditable } from 'helpers/moneyflowHelpers'
import TransactionEditForm from 'components/molecules/forms/TransactionEditForm'
import { OverlayMobile } from 'components/features/pfm/Overlay/OverlayMobile'
import withContext from 'hocs/withContext/withContext'
import { TransactionCategory } from 'components/features/pfm/TransactionCategory/TransactionCategory'
import { PENDING_TRANSACTIONS_HINT } from 'constants/hints'
import { TransactionName } from 'components/features/transactions/TransactionName/TransactionName'
import { TransactionDetailsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'

const DATE_FORMAT = 'MMM DD, YYYY'

type TransactionRowProps = FullMoneyflowTyped & {
  table: TableContextType
  row: Transaction
  isEditing: boolean

  onReadonlyCategoryClick: () => void
  onEditClick: () => void
  onClose: () => void
}

class TransactionRowMobile extends Component<TransactionRowProps> {
  render () {
    const {
      type,
      row,
      isEditing,
      onEditClick,
      onReadonlyCategoryClick,
      onClose,
    } = this.props
    const { rowSpanGrid } = this.props.table
    const { date, amount, status } = row

    const renderActionButton = () => {
      if (isStatusCardTransaction(row)) {
        return <TransactionDetailsPaneButton text='Details' uid={ row.uid } style='textOnly' className={ styles.actionButton } />
      } else if (isTransactionEditable(row)) {
        return <TextButton size='regular' onClick={ onEditClick } className={ styles.actionButton }>Edit</TextButton>
      }
      return null
    }

    const rowKey = this.props.table.getRowKey(row)
    const rowSpan: number = get(rowSpanGrid, `date.${ rowKey }`)

    const formattedDate = formatRelativeDate(date, DATE_FORMAT)
    const rowSpanExists = isNumber(rowSpan)

    const spanRow = rowSpanExists && rowSpan >= 1 ? (
      <div className={ commonRowMobileStyles.spanRow }>{ formattedDate }</div>
    ) : null

    const showDate = !rowSpanExists

    const editForm = isEditing ? (
      <OverlayMobile onClose={ onClose }>
        <TransactionEditForm isMobile transaction={ row } type={ type } />
      </OverlayMobile>
    ) : null

    const isPending = status === 'pending'

    return (
      <tr key={ rowKey }>
        <td>
          { spanRow }
          <div className={ commonRowMobileStyles.commonRowMobile }>
            <div className={ commonRowMobileStyles.line }>
              <div className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.left) }>
                { this.renderDescription() }
                <div className={ commonRowMobileStyles.subtitle }>
                  <TransactionName transaction={ row } />
                </div>
              </div>
              <div className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.right) }>
                <div className={ commonRowMobileStyles.value }>{ formatTableCurrency(amount, 'penny') }</div>
                { showDate && (
                  <div className={ commonRowMobileStyles.subvalue }>{ formattedDate }</div>
                )}
              </div>
            </div>

            <div className={ commonRowMobileStyles.ctaLine }>
              <div className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.left) }>
                <div className={ styles.category }>
                  <TransactionCategory
                    category={ row.category }
                    onClick={ onReadonlyCategoryClick }
                    dim={ isPending }
                    disabled={ isPending }
                    tooltipText={ isPending ? PENDING_TRANSACTIONS_HINT : undefined }
                  />
                </div>
              </div>
              <small className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.right) }>
                { renderActionButton() }
              </small>
            </div>
            { editForm }
          </div>
        </td>
      </tr>
    )
  }

  renderDescription = () => {
    const { row } = this.props
    const { description } = row

    const simpleDescription = description
      || (
        <AbsentValue>Not presented</AbsentValue>
      )

    return (
      <div className={ cn(commonRowMobileStyles.title, styles.description) }>
        <span>{ simpleDescription }</span>
        {
          row.account_source_type === 'yodlee' && row.description && row.description_verbose ? (
            <>
              &nbsp;
              <div className={ styles.tooltipIconWrapperOuter }>
                <div className={ styles.tooltipIconWrapper }>
                  <TooltipIcon icon='ellipsis'>{ row.description_verbose }</TooltipIcon>
                </div>
              </div>
            </>
          ) : null
        }
      </div>
    )
  }
}

export default withContext(TableContext, 'table')(TransactionRowMobile)
